import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Timer } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { alertsFromError, LOANER_REASON_SHORT, type LoanerOptions, type LoanerReason, type TicketLoaner } from '../lib/checkouts';
import { formatDate, formatDateTime } from '../lib/format';
import { invalidateInventory } from '../lib/hooks';
import type { Checkout, PersonSummary } from '../lib/types';
import { DueBadge } from './badges';
import { alertGate, BorrowerAlertsPanel, useBorrowerAlerts } from './borrower-alerts';
import { toLocalInput } from './device-history';
import { PersonPicker } from './pickers';
import { useToast } from './toast';
import { Alert, Badge, Button, Card, CardHeader, Field, Input, Modal, Segmented, Select, Textarea } from './ui';

/** Lends a device from the loaner pool: while someone's own device is repaired (with its ticket), for the day, or otherwise. */
export function LendLoanerModal({
  open,
  onClose,
  ticket,
  person: initialPerson,
  asset,
  onDone,
}: {
  open: boolean;
  onClose(): void;
  /** The ticket it's lent from. Its requester borrows it unless someone else is chosen. */
  ticket?: { id: string; number: number; title: string; requesterPerson: PersonSummary | null } | null;
  person?: PersonSummary | null;
  /** A loaner already chosen, as at the check in / out station. */
  asset?: { id: string; assetTag: string; name: string } | null;
  onDone?(checkout: Checkout): void;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [person, setPerson] = useState<PersonSummary | null>(null);
  const [reason, setReason] = useState<LoanerReason>('REPAIR');
  const [ticketId, setTicketId] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [due, setDue] = useState('');
  const [dueChanged, setDueChanged] = useState(false);
  const [notes, setNotes] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  // A fresh form each time it opens.
  useEffect(() => {
    if (!open) return;
    setPerson(initialPerson ?? ticket?.requesterPerson ?? null);
    setReason(ticket ? 'REPAIR' : 'FORGOT_DEVICE');
    setTicketId(ticket?.id ?? '');
    setDeviceId(asset?.id ?? '');
    setDue('');
    setDueChanged(false);
    setNotes('');
    setConfirmed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const options = useQuery({
    queryKey: ['loaner-options', person?.id ?? null, ticket?.id ?? null],
    queryFn: () => api.get<LoanerOptions>('/loaners/options', { personId: person?.id, workOrderId: ticket?.id }),
    enabled: open,
  });
  const defaults = options.data?.defaults;

  // Due back at the end of the day for a forgotten device, or after the usual repair loan, until someone changes it.
  useEffect(() => {
    if (defaults && !dueChanged) setDue(toLocalInput(reason === 'FORGOT_DEVICE' ? defaults.sameDayDueAt : defaults.repairDueAt));
  }, [defaults, reason, dueChanged]);

  const alerts = useBorrowerAlerts(person?.id, { kind: 'LOANER', enabled: open });
  useEffect(() => setConfirmed(false), [person?.id]);
  const gate = alertGate(alerts.data, confirmed, can('ADMIN'));

  const tickets = options.data?.tickets ?? [];
  const ticketChoices = options.data?.ticket && !tickets.some((row) => row.id === options.data!.ticket!.id) ? [options.data.ticket, ...tickets] : tickets;
  const devices = options.data?.devices ?? [];
  const dueIso = due && !Number.isNaN(Date.parse(due)) ? new Date(due).toISOString() : null;
  const needsTicket = reason === 'REPAIR';
  const ready = !!person && !!deviceId && !!dueIso && (!needsTicket || !!ticketId) && !alerts.isLoading && gate.ok;

  const lend = useMutation({
    mutationFn: () =>
      api.post<{ checkouts: Checkout[] }>('/checkouts', {
        assetIds: [deviceId],
        assignee: { type: 'PERSON', personId: person!.id },
        kind: 'LOANER',
        loanerReason: reason,
        workOrderId: reason === 'FORGOT_DEVICE' ? null : ticketId || null,
        dueAt: dueIso,
        notes: notes || undefined,
        overrideAlerts: gate.override,
      }),
    onSuccess: ({ checkouts }) => {
      invalidateInventory(queryClient);
      const [checkout] = checkouts;
      toast.success(`Lent loaner ${checkout.asset.assetTag} to ${checkout.assigneeLabel}`);
      onDone?.(checkout);
      onClose();
    },
    onError: (err) => {
      if (alertsFromError(err)) void alerts.refetch();
      toast.error(errorMessage(err));
    },
  });

  const firstName = person ? person.preferredName?.trim() || person.firstName : '';

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title="Lend a loaner"
      description={ticket ? `For ticket #${ticket.number}: ${ticket.title}` : 'A device from the loaner pool, with a date it’s due back'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" icon={<Timer />} disabled={!ready} loading={lend.isPending} onClick={() => lend.mutate()}>
            Lend loaner
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <Field as="div" label="Borrower" required>
          <PersonPicker value={person} onChange={setPerson} placeholder="Search for the student or staff member" />
        </Field>
        <BorrowerAlertsPanel alerts={alerts.data} loading={!!person && alerts.isLoading} confirmed={confirmed} onConfirm={setConfirmed} />

        <Field as="div" label="Why it’s lent">
          <Segmented<LoanerReason>
            value={reason}
            onChange={setReason}
            options={[
              { value: 'REPAIR', label: 'Device in repair' },
              { value: 'FORGOT_DEVICE', label: 'Forgot it today' },
              { value: 'OTHER', label: 'Other' },
            ]}
          />
        </Field>

        {reason === 'FORGOT_DEVICE' ? (
          <p className="-mt-2 text-xs text-slate-500">A loaner for a forgotten device isn’t linked to a ticket.</p>
        ) : (
          <Field
            label="Ticket"
            required={needsTicket}
            hint={
              person && options.data && ticketChoices.length === 0
                ? `${firstName} has no open technology tickets. Open one for the repair first.`
                : 'Open technology tickets they requested. The loaner shows on the ticket.'
            }
          >
            <Select value={ticketId} onChange={(event) => setTicketId(event.target.value)}>
              <option value="">{needsTicket ? 'Choose the ticket for the repair' : 'No ticket'}</option>
              {ticketChoices.map((row) => (
                <option key={row.id} value={row.id}>
                  #{row.number} {row.title}
                  {row.asset ? ` (${row.asset.assetTag})` : ''}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {asset ? (
          <Field label="Loaner">
            <div className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
              <span className="font-mono font-semibold text-slate-900">{asset.assetTag}</span> · {asset.name}
            </div>
          </Field>
        ) : (
          <>
            <Field label="Loaner" required hint={options.data ? `${devices.length} of ${options.data.poolSize} in the pool on the shelf` : undefined}>
              <Select value={deviceId} onChange={(event) => setDeviceId(event.target.value)} disabled={devices.length === 0}>
                <option value="">Choose a loaner</option>
                {devices.map((device) => (
                  <option key={device.id} value={device.id}>
                    {device.assetTag} · {device.name}
                  </option>
                ))}
              </Select>
            </Field>
            {options.data && devices.length === 0 && (
              <Alert tone="amber" title="No loaners on the shelf">
                {options.data.poolSize === 0
                  ? 'Add devices to the loaner pool: edit a device and check “In the loaner pool.”'
                  : 'Every loaner is out. Check one in to lend it again.'}
              </Alert>
            )}
          </>
        )}

        <Field label="Due back" required hint={dueIso ? `Due ${formatDateTime(dueIso)}` : undefined}>
          <Input
            type="datetime-local"
            value={due}
            min={toLocalInput(new Date())}
            onChange={(event) => {
              setDue(event.target.value);
              setDueChanged(true);
            }}
          />
          {defaults && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {[
                { label: 'End of the school day', value: defaults.sameDayDueAt },
                { label: `${defaults.loanerDays} day${defaults.loanerDays === 1 ? '' : 's'}`, value: defaults.repairDueAt },
              ].map((option) => (
                <button
                  key={option.label}
                  type="button"
                  onClick={() => {
                    setDue(toLocalInput(option.value));
                    setDueChanged(true);
                  }}
                  className={
                    due === toLocalInput(option.value)
                      ? 'rounded-full bg-brand-700 px-2.5 py-1 text-xs font-medium text-white'
                      : 'rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200'
                  }
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </Field>

        <Field label="Notes">
          <Textarea rows={2} value={notes} maxLength={2000} onChange={(event) => setNotes(event.target.value)} placeholder="Charger included, condition when it went out…" />
        </Field>
      </div>
    </Modal>
  );
}

/** The loaners lent for a ticket, on the ticket. */
export function TicketLoanersCard({
  ticket,
  loaners,
}: {
  ticket: { id: string; number: number; title: string; requesterPerson: PersonSummary | null };
  loaners: TicketLoaner[];
}) {
  const [lending, setLending] = useState(false);
  const out = loaners.filter((loaner) => !loaner.checkedInAt).length;

  return (
    <Card>
      <CardHeader
        title="Loaners"
        description={out > 0 ? `${out} out for this ticket` : 'Lend one while the device is repaired'}
        actions={
          <Button size="sm" icon={<Timer />} onClick={() => setLending(true)}>
            Lend a loaner
          </Button>
        }
      />
      {loaners.length === 0 ? (
        <p className="px-5 py-4 text-sm text-slate-500">No loaners lent for this ticket.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {loaners.map((loaner) => (
            <li key={loaner.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-3 text-sm">
              <Link to={`/assets/${loaner.asset.id}`} className="font-mono text-xs font-semibold text-brand-800 hover:underline">
                {loaner.asset.assetTag}
              </Link>
              <span className="min-w-0 flex-1 truncate text-slate-700">{loaner.assigneeLabel}</span>
              {loaner.loanerReason && loaner.loanerReason !== 'REPAIR' && <Badge>{LOANER_REASON_SHORT[loaner.loanerReason]}</Badge>}
              {loaner.checkedInAt ? <span className="text-xs text-slate-500">Returned {formatDate(loaner.checkedInAt)}</span> : <DueBadge dueAt={loaner.dueAt} />}
            </li>
          ))}
        </ul>
      )}
      <LendLoanerModal open={lending} onClose={() => setLending(false)} ticket={ticket} />
    </Card>
  );
}
